/**
 * High-level operation permission checker
 * Maps common operations to backend permission strings
 * 
 * @module hooks/useCanPerform
 */

import type { OperationKey, PermissionString } from '../types/permissions';
import { usePermission, useHasAllPermissions } from './usePermission';
import { useAuth } from '../context/authcontext';

/**
 * Mapping of high-level operations to backend permission codes
 * 
 * This central mapping makes frontend code more readable than raw permission strings
 * Example: `useCanPerform('create:visit')` is clearer than checking `'patientmr:phi:visit:create'`
 * 
 * Update this as new operations/permissions are added to the backend
 */
const OPERATION_PERMISSION_MAP: Record<OperationKey, PermissionString | PermissionString[]> = {
  // Visit operations (Patient Medical Record)
  'create:visit': 'patientmr:phi:visit:create' as PermissionString,
  'read:visit': 'patientmr:phi:visit:read' as PermissionString,
  'update:visit': 'patientmr:phi:visit:update' as PermissionString,
  'delete:visit': 'patientmr:phi:visit:delete' as PermissionString,

  // Insurance operations (Patient Medical Record)
  'read:insurance': 'patientmr:phi:insurance:read' as PermissionString,
  'create:insurance': 'patientmr:phi:insurance:create' as PermissionString,
  'update:insurance': 'patientmr:phi:insurance:update' as PermissionString,
  'delete:insurance': 'patientmr:phi:insurance:delete' as PermissionString,

  // Prior Authorization/Supervision operations
  'create:authorization': 'patientmr:phi:authorization:create' as PermissionString,
  'approve:authorization': [
    'patientmr:phi:authorization:approve',
    'patientmr:phi:supervisor:approve',
  ] as PermissionString[],
  'deny:authorization': 'patientmr:phi:authorization:deny' as PermissionString,

  // Billing/Claims operations
  'read:claims': 'billing:claims:read' as PermissionString,
  'create:claims': 'billing:claims:create' as PermissionString,
  'approve:claims': 'billing:claims:approve' as PermissionString,
  'delete:claims': 'billing:claims:delete' as PermissionString,

  // Assessment operations
  'create:assessment': 'patientmr:phi:assessment:create' as PermissionString,
  'read:assessment': 'patientmr:phi:assessment:read' as PermissionString,
  'update:assessment': 'patientmr:phi:assessment:update' as PermissionString,

  // Care Plan operations
  'create:careplan': 'patientmr:phi:careplan:create' as PermissionString,
  'read:careplan': 'patientmr:phi:careplan:read' as PermissionString,
  'update:careplan': 'patientmr:phi:careplan:update' as PermissionString,

  // Admin operations
  'manage:users': 'admin:users:manage' as PermissionString,
  'manage:roles': 'admin:roles:manage' as PermissionString,
  'view:audit:log': 'admin:audit:view' as PermissionString,
  'download:audit:log': 'admin:audit:phi:download' as PermissionString,
  // Phase 14 data exports (owner, admin)
  'export:patient': 'admin:export:patient' as PermissionString,
  'export:agency': 'admin:export:agency' as PermissionString,
  'manage:subscription': 'admin:subscription:manage' as PermissionString,

  // Discharge / transfer summaries (Phase 16E, DSC-22); the backend also checks patient scope and authorship
  'read:dischargesummary': 'patientmr:phi:dischargesummary:read' as PermissionString,
  'update:dischargesummary': 'patientmr:phi:dischargesummary:update' as PermissionString,
  'sign:dischargesummary': 'patientmr:phi:dischargesummary:sign' as PermissionString,
  'qa:dischargesummary': 'patientmr:phi:dischargesummary:qa' as PermissionString,
  'send:dischargesummary': 'patientmr:phi:dischargesummary:send' as PermissionString,

  // Critical notifications (Phase 16F, plan §4 J): self inbox for staff; settings for owner/admin
  'read:notifications': 'notifications:self:read' as PermissionString,
  'update:notifications': 'notifications:self:update' as PermissionString,
  'manage:notificationsettings': 'admin:notifications:settings:update' as PermissionString,

  // Admin user operations
  'read:users:pii': 'admin:users:pii:read' as PermissionString,
  'create:users:pii': 'admin:users:pii:create' as PermissionString,
  'update:users:pii': 'admin:users:pii:update' as PermissionString,
  'update:users:nonpii': 'admin:users:nonpii:update' as PermissionString,

// Admin agency operations
    'read:agencies': 'admin:agencies:read' as PermissionString,
    'create:agencies': 'admin:agencies:create' as PermissionString,
    'update:agencies': 'admin:agencies:update' as PermissionString,

  // Admin config operations
  'read:config': 'admin:config:read' as PermissionString,
  'write:config': 'admin:config:write' as PermissionString,

  // Family Portal staff operations (Phase 12: invites, revoke; approval and age-18 review)
  'manage:guardians': 'admin:patient:familyportal:write' as PermissionString,
  'view:guardians': 'admin:patient:familyportal:read' as PermissionString,
  'approve:guardians': 'admin:patient:familyportal:approve' as PermissionString,

  // Admin patient operations (mapped to backend permissions)
  'read:patients:nonphi': 'admin:patients:nonphi:read' as PermissionString,
  'create:patients:nonphi': 'admin:patients:nonphi:create' as PermissionString,
  'create:patients:phi': 'admin:patients:phi:create' as PermissionString,
  'update:patients:nonphi': 'admin:patients:nonphi:update' as PermissionString,
  'update:patients:phi': 'admin:patients:phi:update' as PermissionString,
  'read:patients:familyportal': 'admin:patients:familyportal:read' as PermissionString,
  'write:patients:familyportal': 'admin:patients:familyportal:write' as PermissionString,
  'read:patients:phi:all': 'admin:patients:phi:readall' as PermissionString,
  'admit:patients': 'admin:patients:admit' as PermissionString,
  'discharge:patients': 'admin:patients:discharge' as PermissionString,

  // Family portal guardian reads (Phase 12: guardians hold only this plus self-profile)
  'read:portal': 'portal:read' as PermissionString,
};

/**
 * Check if user can perform a specific operation
 * 
 * This is the main hook to use in components for common operations.
 * It translates operation names to backend permissions and checks authorization.
 *
 * @param operation - Operation key (e.g., 'create:visit', 'approve:authorization')
 * @returns true if user can perform the operation
 * 
 * @example
 * const canCreateVisit = useCanPerform('create:visit');
 * const canApproveAuth = useCanPerform('approve:authorization');
 * 
 * // In component:
 * <button disabled={!canCreateVisit}>Create Visit</button>
 */
export const useCanPerform = (operation: OperationKey): boolean => {
  const permission = OPERATION_PERMISSION_MAP[operation];

  // Always call both hooks at the top level with safe defaults
  // (usePermission with empty array for invalid single permission case)
  const singlePermissionResult = usePermission(
    typeof permission === 'string' ? permission : ([] as PermissionString[])
  );
  const multiPermissionResult = usePermission(
    Array.isArray(permission) ? (permission as PermissionString[]) : ([] as PermissionString[])
  );

  if (!permission) {
    console.warn(`Unknown operation: ${operation}`);
    return false;
  }

  // Select the correct result based on permission type
  if (Array.isArray(permission)) {
    return multiPermissionResult;
  }
  return singlePermissionResult;
};

/**
 * Check if user can perform multiple operations (all required)
 * 
 * Use when an action requires multiple capabilities
 *
 * @param operations - Array of operation keys to check
 * @returns true only if user can perform all operations
 * 
 * @example
 * const canManageAuthorizations = useCanPerformAll([
 *   'create:authorization',
 *   'approve:authorization'
 * ]);
 */
export const useCanPerformAll = (operations: OperationKey[]): boolean => {
  const permissions = operations
    .map((op) => OPERATION_PERMISSION_MAP[op])
    .flat() as PermissionString[];

  // Always call hook at top level with safe default
  const allPermissionsResult = useHasAllPermissions(permissions.length > 0 ? permissions : []);

  if (permissions.length === 0) {
    return false;
  }
  return allPermissionsResult;
};

/**
 * Get all operations user can perform
 * 
 * Useful for filtering UI elements or building capability lists
 *
 * @returns Array of operations the user has permission to perform
 * 
 * @example
 * const performableOps = usePerformableOperations();
 * 
 * const buttons = performableOps
 *   .filter(op => op.includes('visit'))
 *   .map(op => <ActionButton key={op}>{op}</ActionButton>);
 */
export const usePerformableOperations = (): OperationKey[] => {
  const { user } = useAuth();
  const operations = Object.keys(OPERATION_PERMISSION_MAP) as OperationKey[];

  if (!user?.permissions) return [];

  // Cast user permissions to PermissionString[] for proper type checking
  const userPermissions = user.permissions as PermissionString[];

  return operations.filter((op) => {
    const permission = OPERATION_PERMISSION_MAP[op];
    if (!permission) return false;

    if (Array.isArray(permission)) {
      return permission.some((p) => userPermissions.includes(p));
    }
    return userPermissions.includes(permission);
  });
};

/**
 * Check if operation exists in the operation map
 * 
 * Use in development to validate operation names
 *
 * @param operation - Operation name to validate
 * @returns true if operation is defined
 */
export const isValidOperation = (operation: string): operation is OperationKey => {
  return operation in OPERATION_PERMISSION_MAP;
};
